import React, { useMemo, useState, useEffect } from 'react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useRevenueView } from '../context/RevenueViewContext';
import { fmtMoney } from '../lib/format';
import { amountForView, REVENUE_VIEWS, dealTalentIds } from '../lib/deals';
import { filterByRange } from '../lib/dateRange';
import DateRangeFilter from '../components/DateRangeFilter';
import { Search, Plus, LayoutGrid, List, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import DealsKanban from '../components/deals/DealsKanban';
import DealsTable from '../components/deals/DealsTable';
import DealDialog from '../components/deals/DealDialog';
import QuickAddDeal from '../components/deals/QuickAddDeal';

const businessDaysSince = (fromDate) => {
  const from = new Date(fromDate);
  from.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let count = 0;
  const cur = new Date(from);
  while (cur < today) {
    cur.setDate(cur.getDate() + 1);
    const day = cur.getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
};

const StatCard = ({ label, value, hint, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${accent || 'text-slate-900'}`}>{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
  </div>
);

const Deals = () => {
  const { deals, talent, brands, loading } = useDeals();
  const { user } = useAuth();
  const { view, setView } = useRevenueView();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  const [boardView, setBoardView] = useState(() => {
    try { return localStorage.getItem('deals.boardView') || 'kanban'; } catch { return 'kanban'; }
  });
  useEffect(() => {
    try { localStorage.setItem('deals.boardView', boardView); } catch {}
  }, [boardView]);
  const [dateRange, setDateRange] = useState('all');
  const [activeOnly, setActiveOnly] = useState(() => {
    try { return localStorage.getItem('deals.activeOnly') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('deals.activeOnly', activeOnly ? '1' : '0'); } catch {}
  }, [activeOnly]);
  const [chaseOnly, setChaseOnly] = useState(() => {
    try { return localStorage.getItem('deals.chaseOnly') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('deals.chaseOnly', chaseOnly ? '1' : '0'); } catch {}
  }, [chaseOnly]);
  const [confirmedOnly, setConfirmedOnly] = useState(() => {
    try { return localStorage.getItem('deals.confirmedOnly') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('deals.confirmedOnly', confirmedOnly ? '1' : '0'); } catch {}
  }, [confirmedOnly]);
  const [q, setQ] = useState('');
  const [talentId, setTalentId] = useState('all');
  const [brandId, setBrandId] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [defaultStage, setDefaultStage] = useState('conversation');

  const filtered = useMemo(() => {
    const inDateRange = filterByRange(deals, dateRange, 'created_at');
    return inDateRange.filter((d) => {
      if (activeOnly && d.stage === 'complete') return false;
      if (chaseOnly) {
        const lastTouched = d.updated_at || d.created_at;
        if (!lastTouched || businessDaysSince(lastTouched) < 3) return false;
      }
      const ids = dealTalentIds(d);
      if (talentId !== 'all' && !ids.includes(talentId)) return false;
      if (brandId !== 'all' && d.brand_id !== brandId) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        const talentNames = ids.map((id) => talent.find((x) => x.user_id === id)?.name || '').join(' ');
        const b = brands.find((x) => x.id === d.brand_id);
        return (
          (d.title || '').toLowerCase().includes(s) ||
          talentNames.toLowerCase().includes(s) ||
          (b?.name || '').toLowerCase().includes(s) ||
          (d.type || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [deals, q, talentId, brandId, talent, brands, dateRange, activeOnly, chaseOnly]);

  // List view hides declined deals (they only live in the board's far-right column). The
  // "confirmed deals" toggle narrows the list to booked stages only.
  const CONFIRMED_STAGES = ['rate_confirmed', 'contracted', 'complete'];
  const listDeals = useMemo(() => {
    let ds = filtered.filter((d) => d.stage !== 'declined');
    if (confirmedOnly) ds = ds.filter((d) => CONFIRMED_STAGES.includes(d.stage));
    return ds;
  }, [filtered, confirmedOnly]);

  const [monthOffset, setMonthOffset] = useState(0);  // 0 = current month, -1 = last, +1 = next, etc.

  const totals = useMemo(() => {
    const RATE_CONFIRMED_STAGES = ['rate_confirmed', 'contracted', 'complete'];
    const now = new Date();
    const focusMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const inRateConfirmed = (d) => RATE_CONFIRMED_STAGES.includes(d.stage);
    const inFocusMonth = (d) => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt.getFullYear() === focusMonth.getFullYear() && dt.getMonth() === focusMonth.getMonth();
    };
    const inSameYear = (d) => {
      if (!d.created_at) return false;
      return new Date(d.created_at).getFullYear() === now.getFullYear();
    };
    const sum = (arr) => arr.reduce((s, d) => {
      const primaryId = dealTalentIds(d)[0];
      const primaryTalent = primaryId ? talent.find((t) => t.user_id === primaryId) : null;
      return s + amountForView(d, view, primaryTalent);
    }, 0);
    return {
      count: filtered.length,
      focusMonthRateConfirmed: sum(filtered.filter((d) => inRateConfirmed(d) && inFocusMonth(d))),
      yearRateConfirmed: sum(filtered.filter((d) => inRateConfirmed(d) && inSameYear(d))),
      focusMonthLabel: focusMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' }),
      focusMonthValue: focusMonth,
      currentYear: now.getFullYear(),
    };
  }, [filtered, view, talent, monthOffset]);

  // Build the list of month options (12 back, 2 forward from today) for the dropdown
  const monthOptions = useMemo(() => {
    const now = new Date();
    const opts = [];
    for (let o = 2; o >= -12; o -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() + o, 1);
      opts.push({ offset: o, label: d.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    return opts;
  }, []);

  const openNew = (stage) => { if (!isManager) return; setDefaultStage(stage || 'conversation'); setQuickOpen(true); };
  const openEdit = (deal) => { setEditingDeal(deal); setDialogOpen(true); };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">deals</h1>
          <p className="text-sm text-slate-500 mt-1">{isManager ? 'your entire pipeline in one place. drag to update stage.' : 'here’s everything currently on your plate.'}</p>
        </div>
        {isManager && (
          <Button onClick={() => openNew('conversation')} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> new deal
          </Button>
        )}
      </header>

      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-sm">
          {REVENUE_VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              title={v.hint}
              className={`px-3 h-9 inline-flex items-center gap-1.5 ${view === v.id ? 'bg-mint-50 text-mint-700 font-medium' : 'text-slate-600 hover:bg-slate-50'} ${v.id !== 'full' ? 'border-l border-slate-200' : ''}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 hidden md:inline">showing <span className="font-medium text-slate-600">{REVENUE_VIEWS.find((v) => v.id === view)?.hint}</span></span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{totals.focusMonthLabel} Deals</div>
            <Select value={String(monthOffset)} onValueChange={(v) => setMonthOffset(parseInt(v, 10))}>
              <SelectTrigger className="h-7 w-40 text-xs" data-testid="deals-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.offset} value={String(m.offset)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-1 text-2xl font-semibold text-pink-600" data-testid="deals-month-total">{fmtMoney(totals.focusMonthRateConfirmed)}</div>
          <div className="text-xs text-slate-500 mt-0.5">rate confirmed · contracted · complete</div>
        </div>
        <StatCard label={`${totals.currentYear} Deals`} value={fmtMoney(totals.yearRateConfirmed)} hint="rate confirmed · contracted · complete" accent="text-pink-600" />
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search deals, talent, brands…" className="pl-9 h-10 bg-white" />
        </div>

        {isManager && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={talentId} onValueChange={setTalentId}>
              <SelectTrigger className="h-10 w-40 bg-white"><SelectValue placeholder="talent" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all talent</SelectItem>
                {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={brandId} onValueChange={setBrandId}>
              <SelectTrigger className="h-10 w-40 bg-white"><SelectValue placeholder="brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="ml-auto inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button onClick={() => setBoardView('kanban')} className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm ${boardView === 'kanban' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}>
            <LayoutGrid className="w-4 h-4" /> board
          </button>
          <button onClick={() => setBoardView('table')} className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm border-l border-slate-200 ${boardView === 'table' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}>
            <List className="w-4 h-4" /> list
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5 md:-mt-2">
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        <button
          onClick={() => setChaseOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${chaseOnly ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-600'}`}
          title="only deals with no changes made in the last 3 working days"
          data-testid="deals-chase-toggle"
          aria-pressed={chaseOnly}
        >
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${chaseOnly ? 'bg-white' : 'bg-amber-500'}`} />
          chase
        </button>
        <button
          onClick={() => setActiveOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${activeOnly ? 'bg-pink-500 text-white border-pink-500 hover:bg-pink-600' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600'}`}
          title="only deals that aren't complete"
          data-testid="deals-active-only-toggle"
          aria-pressed={activeOnly}
        >
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${activeOnly ? 'bg-white' : 'bg-emerald-500'}`} />
          active deals
        </button>
        {boardView === 'table' && (
          <button
            onClick={() => setConfirmedOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${confirmedOnly ? 'bg-violet-500 text-white border-violet-500 hover:bg-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300 hover:text-violet-600'}`}
            title="only rate confirmed, contracted & complete deals"
            data-testid="deals-confirmed-only-toggle"
            aria-pressed={confirmedOnly}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${confirmedOnly ? 'bg-white' : 'bg-violet-500'}`} />
            confirmed deals
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-12 text-center">loading…</div>
      ) : boardView === 'kanban' ? (
        <DealsKanban filteredDeals={filtered} onCardClick={openEdit} onAdd={openNew} canAdd={isManager} />
      ) : (
        <DealsTable filteredDeals={listDeals} onCardClick={openEdit} groupByTalent />
      )}

      <DealDialog open={dialogOpen} onOpenChange={setDialogOpen} deal={editingDeal} defaultStage={defaultStage} />
      <QuickAddDeal open={quickOpen} onOpenChange={setQuickOpen} defaultStage={defaultStage} />
    </div>
  );
};

export default Deals;
